import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  CreditCard,
  User,
  FileText,
  Loader2,
  Calendar,
  Building2,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  Printer,
  Banknote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { PaymentAllocationsCard } from '@/components/trading/payment-allocations-card';
import { cn } from '@/lib/utils';

const paymentTypeConfig: Record<string, { color: string; icon: typeof CreditCard; label: string }> = {
  CUSTOMER_PAYMENT: { color: 'bg-green-100 text-green-700', icon: ArrowDownLeft, label: 'Customer Payment' },
  SUPPLIER_PAYMENT: { color: 'bg-blue-100 text-blue-700', icon: ArrowUpRight, label: 'Supplier Payment' },
};

const paymentMethodConfig: Record<string, string> = {
  CASH: 'Cash',
  CHECK: 'Check',
  BANK_TRANSFER: 'Bank Transfer',
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  PAYPAL: 'PayPal',
  OTHER: 'Other',
};

export function PaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();

  const { data: payment, isLoading, error } = trpc.tradingPayment.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatDateTime = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/finance/payments')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('finance.payments.backToList')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('finance.payments.notFound')}</p>
        </div>
      </div>
    );
  }

  const typeConfig = paymentTypeConfig[payment.type] ?? paymentTypeConfig.CUSTOMER_PAYMENT!;
  const TypeIcon = typeConfig.icon;
  const isCustomerPayment = payment.type === 'CUSTOMER_PAYMENT';

  // Determine customer/supplier info
  const customerName = payment.tradingCustomer?.companyName || payment.invoice?.tradingCustomer?.companyName || null;
  const customerContactName = payment.tradingCustomer?.contactName || payment.invoice?.tradingCustomer?.contactName || null;
  const customerId = payment.tradingCustomerId || payment.invoice?.tradingCustomerId || null;

  const supplierName = payment.supplier?.companyName || payment.supplierInvoice?.supplier?.companyName || null;
  const supplierId = payment.supplierId || payment.supplierInvoice?.supplierId || null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/finance/payments')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('finance.payments.backToList')}
          </Button>
          <div className="flex items-center gap-3">
            <div className={cn(
              'flex h-12 w-12 items-center justify-center',
              isCustomerPayment ? 'bg-green-100' : 'bg-blue-100'
            )}>
              <CreditCard className={cn(
                'h-6 w-6',
                isCustomerPayment ? 'text-green-600' : 'text-blue-600'
              )} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold font-mono">{payment.paymentNumber}</h1>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                    typeConfig.color
                  )}
                >
                  <TypeIcon className="h-3 w-3" />
                  {isCustomerPayment ? t('finance.payments.typeCustomer') : t('finance.payments.typeSupplier')}
                </span>
                <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 text-xs font-medium">
                  <CheckCircle className="h-3 w-3" />
                  {t('finance.payments.active')}
                </span>
              </div>
              <p className="text-muted-foreground">
                {t('finance.payments.recordedOn')} {formatDateTime(payment.createdAt)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Printer className="mr-2 h-4 w-4" />
            {tCommon('print')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Payment Details */}
        <div className="border border-border bg-card p-6">
          <h2 className="flex items-center gap-2 font-semibold mb-4">
            <CreditCard className="h-5 w-5" />
            {t('finance.payments.paymentDetails')}
          </h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.paymentNumber')}</span>
              <span className="font-mono font-medium">{payment.paymentNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.amount')}</span>
              <span className="font-bold text-lg">{formatCurrency(payment.amount, payment.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.currency')}</span>
              <span className="font-medium">{payment.currency}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.method')}</span>
              <span className="font-medium">{paymentMethodConfig[payment.paymentMethod] || payment.paymentMethod}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.date')}</span>
              <span className="font-medium">{formatDate(payment.paymentDate)}</span>
            </div>
          </div>
        </div>

        {/* Customer/Supplier Info */}
        <div className="border border-border bg-card p-6">
          <h2 className="flex items-center gap-2 font-semibold mb-4">
            {isCustomerPayment ? (
              <>
                <User className="h-5 w-5" />
                {t('finance.payments.customerInfo')}
              </>
            ) : (
              <>
                <Building2 className="h-5 w-5" />
                {t('finance.payments.supplierInfo')}
              </>
            )}
          </h2>
          <div className="space-y-3 text-sm">
            {isCustomerPayment ? (
              <>
                <div>
                  <span className="text-muted-foreground">{t('finance.payments.company')}</span>
                  {customerId ? (
                    <Link
                      to={`/trading/customers/${customerId}`}
                      className="block font-medium text-primary hover:underline"
                    >
                      {customerName || '-'}
                    </Link>
                  ) : (
                    <p className="font-medium">{customerName || '-'}</p>
                  )}
                </div>
                {customerContactName && (
                  <div>
                    <span className="text-muted-foreground">{t('finance.payments.contactName')}</span>
                    <p className="font-medium">{customerContactName}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <span className="text-muted-foreground">{t('finance.payments.supplier')}</span>
                  {supplierId ? (
                    <Link
                      to={`/trading/suppliers/${supplierId}`}
                      className="block font-medium text-primary hover:underline"
                    >
                      {supplierName || '-'}
                    </Link>
                  ) : (
                    <p className="font-medium">{supplierName || '-'}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Additional Details */}
        <div className="border border-border bg-card p-6">
          <h2 className="flex items-center gap-2 font-semibold mb-4">
            <Calendar className="h-5 w-5" />
            {t('finance.payments.additionalDetails')}
          </h2>
          <div className="space-y-3 text-sm">
            {payment.reference && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finance.payments.reference')}</span>
                <span className="font-mono font-medium">{payment.reference}</span>
              </div>
            )}
            {payment.bankAccount && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finance.payments.bankAccount')}</span>
                <span className="font-mono font-medium">{payment.bankAccount}</span>
              </div>
            )}
            {payment.createdBy && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finance.payments.recordedBy')}</span>
                <span className="font-medium">
                  {payment.createdBy.firstName} {payment.createdBy.lastName}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.createdAt')}</span>
              <span className="font-medium">{formatDateTime(payment.createdAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Related Invoice(s) */}
      <div className="border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-semibold mb-4">
          <FileText className="h-5 w-5" />
          {isCustomerPayment ? t('finance.payments.relatedInvoice') : t('finance.payments.relatedSupplierInvoice')}
        </h2>

        <PaymentAllocationsCard payment={payment} />

        {payment.allocations.length === 0 && isCustomerPayment && payment.invoice ? (
          <div className="border border-border bg-muted/30 overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('finance.invoices.invoiceNumber')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('finance.invoices.customer')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('finance.invoices.status')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('finance.invoices.total')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('finance.invoices.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link
                      to={`/trading/finance/invoices/${payment.invoice.id}`}
                      className="font-mono text-sm font-medium text-primary hover:underline"
                    >
                      {payment.invoice.invoiceNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {payment.invoice.tradingCustomer?.companyName || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                      payment.invoice.status === 'PAID' ? 'bg-green-100 text-green-700' :
                      payment.invoice.status === 'PARTIALLY_PAID' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    )}>
                      {payment.invoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(Number(payment.invoice.total), payment.invoice.currency)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(Number(payment.invoice.balanceDue), payment.invoice.currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : payment.allocations.length === 0 && !isCustomerPayment && payment.supplierInvoice ? (
          <div className="border border-border bg-muted/30 overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('finance.supplierInvoices.invoiceNumber')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('finance.supplierInvoices.supplier')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('finance.supplierInvoices.status')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('finance.supplierInvoices.total')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('finance.supplierInvoices.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <Link
                      to={`/trading/finance/supplier-invoices/${payment.supplierInvoice.id}`}
                      className="font-mono text-sm font-medium text-primary hover:underline"
                    >
                      {payment.supplierInvoice.invoiceNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {payment.supplierInvoice.supplier?.companyName || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                      payment.supplierInvoice.status === 'PAID' ? 'bg-green-100 text-green-700' :
                      payment.supplierInvoice.status === 'PARTIALLY_PAID' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    )}>
                      {payment.supplierInvoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(Number(payment.supplierInvoice.total), payment.supplierInvoice.currency)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(Number(payment.supplierInvoice.balanceDue), payment.supplierInvoice.currency)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center bg-muted/30 border border-border">
            <FileText className="h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium">{t('finance.payments.noLinkedInvoice')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('finance.payments.noLinkedInvoiceDesc')}
            </p>
          </div>
        )}
      </div>

      {/* Notes */}
      {payment.notes && (
        <div className="border border-border bg-card p-6">
          <h2 className="flex items-center gap-2 font-semibold mb-4">
            <FileText className="h-5 w-5" />
            {t('finance.payments.notes')}
          </h2>
          <p className="text-sm whitespace-pre-wrap">{payment.notes}</p>
        </div>
      )}

      {/* Journal Entry */}
      {payment.journalEntry && (
        <div className="border border-border bg-card p-6">
          <h2 className="flex items-center gap-2 font-semibold mb-4">
            <Banknote className="h-5 w-5" />
            {t('finance.payments.journalEntry')}
          </h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.entryNumber')}</span>
              <Link
                to={`/travel/accounting/journal-entries/${payment.journalEntry.id}`}
                className="font-mono font-medium text-primary hover:underline"
              >
                {payment.journalEntry.entryNumber}
              </Link>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.payments.description')}</span>
              <span className="font-medium">{payment.journalEntry.description || '-'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail */}
      <div className="border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-semibold mb-4">
          <Clock className="h-5 w-5" />
          {t('finance.payments.history')}
        </h2>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-green-100">
              <CheckCircle className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-sm">{t('finance.payments.paymentRecorded')}</p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(payment.createdAt)}
                {payment.createdBy && (
                  <span> - {payment.createdBy.firstName} {payment.createdBy.lastName}</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentDetailPage;
